import { AnyError } from '@if/mui'
import { apiClient } from '@src/services/http-common'
import { InteracPossibleMatch, InteracWire, InteracWireFiltersType } from '@src/types/interac'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

const SCOPE = 'interacWire'

export const interacWireQueryKeys = {
  all: [SCOPE] as const,
  unmatched: () => [...interacWireQueryKeys.all, 'unmatched'] as const,
  unmatchedByFilters: (filters: InteracWireFiltersType) => [...interacWireQueryKeys.unmatched(), filters] as const,
  possibleMatchSearch: (id: string, filters: InteracWireSearchPossibleMatchFilters) =>
    [...interacWireQueryKeys.all, id, 'searchPossibleMatch', filters] as const,
}

const EMPTY_WIRES: InteracWire[] = []
const EMPTY_MATCHES: InteracPossibleMatch[] = []

async function getInteracWireByFilters(filters: InteracWireFiltersType) {
  const response = await apiClient.post<InteracWire[]>('/GetInteracWireByFilters', filters)
  return response.data ?? EMPTY_WIRES
}

export function useInteracWireByFilters(filters: InteracWireFiltersType): [InteracWire[], boolean, AnyError | null] {
  const { data, isFetching, error } = useQuery({
    queryKey: interacWireQueryKeys.unmatchedByFilters(filters),
    queryFn: () => getInteracWireByFilters(filters),
  })
  return [data ?? EMPTY_WIRES, isFetching, error]
}

export interface DeleteInteracWireParams {
  id: string
  versionTag: string
  reason: string
}

async function deleteInteracWire(params: DeleteInteracWireParams) {
  const { data } = await apiClient.put<InteracWire>('/DiscardInteracWire', params)
  return data
}

export function useDeleteInteracWire() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteInteracWire,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: interacWireQueryKeys.unmatched() })
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: interacWireQueryKeys.unmatched() })
    },
  })
}

export interface MatchInteracWireParams {
  id: string
  versionTag: string
  loanId: string
  dateOverride: string | null
}

async function matchInteracWire(params: MatchInteracWireParams) {
  const { data } = await apiClient.post<InteracWire>('/RecordInteracWirePayment', params)
  return data
}

export function useMatchInteracWire() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: matchInteracWire,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: interacWireQueryKeys.unmatched() })
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: interacWireQueryKeys.unmatched() })
    },
  })
}

export interface InteracWireSearchPossibleMatchFilters {
  referenceNumber: string | null
  email: string | null
}

async function searchPossibleMatch(id: string, filters: InteracWireSearchPossibleMatchFilters) {
  const { data } = await apiClient.post<InteracPossibleMatch[]>(`/InteracWire/${id}/SearchPossibleMatch`, filters)
  return data ?? EMPTY_MATCHES
}

export function useSearchPossibleMatch(
  id: string | null,
  filters: InteracWireSearchPossibleMatchFilters | null,
): [InteracPossibleMatch[], boolean, AnyError | null] {
  const { data, isFetching, error } = useQuery({
    queryKey: interacWireQueryKeys.possibleMatchSearch(id!, filters!),
    queryFn: () => searchPossibleMatch(id!, filters!),
    enabled: Boolean(id && filters),
  })
  return [data ?? EMPTY_MATCHES, isFetching, error]
}

async function resetPossibleMatch(id: string) {
  const { data } = await apiClient.put<InteracWire>(`/InteracWire/${id}/ResetPossibleMatch`)
  return data
}

export function useResetInteracPossibleMatch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: resetPossibleMatch,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: interacWireQueryKeys.unmatched() })
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: interacWireQueryKeys.unmatched() })
    },
  })
}
